import { ApplicationFormPageEnum } from '../types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { InputAdornment } from '@components/common/InputAdornment';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentRoute } from '@utils/location';
import { parseApplicationInProgress } from '../helpers';
import { travelSpendingQuestions } from './questions';
import { ITravelExpensesForm } from './types';

export const TravelExpenses = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(id);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<ITravelExpensesForm>({
    defaultValues: {
      travelSpending: '',
      hasTravelSpending: null,
    },
  });

  const formState = form.watch();

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const { travelSpending } = parseApplicationInProgress(applicationInProgress[currentRoute]);

      form.reset({
        travelSpending,
        hasTravelSpending: travelSpending ? 'true' : 'false',
      });
    }
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: ITravelExpensesForm) => {
    const { travelSpending } = formData;

    const questions = Object.entries({ travelSpending }).map(([key, answer]) => ({
      question: intl.formatMessage({ id: travelSpendingQuestions[key].labelKey }),
      key,
      answer: answer || 0,
    }));

    patchApplicationInProgress({ questions, currentRoute, applicationId: id });

    searchParams.set('section', ApplicationFormPageEnum.LOAN_REPAYMENTS);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        {Object.entries(travelSpendingQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}

            {!formState[question.booleanQuestionFormKey as keyof ITravelExpensesForm] ||
            formState[question.booleanQuestionFormKey as keyof ITravelExpensesForm] === 'false' ? (
              <FormControl
                rules={{ required: requiredFieldErrorMessage }}
                name={question.booleanQuestionFormKey}
                radioOptions={question.options}
                radioGroupProps={{ className: 'mt-2' }}
                onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                  form.setValue(question.booleanQuestionFormKey, event.target.value);
                }}
              />
            ) : (
              <FormCurrencyField
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <FormattedMessage id={question.adornmentKey} />
                      </InputAdornment>
                    ),
                  },
                }}
                name={key}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => Number(value) >= 0,
                }}
              />
            )}
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
